import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getCredit,
  GetCreditRequest,
  getPanels,
  GetPanelsRequest,
  getToken,
  registerPanel,
  RegisterPanelRequest,
  requestOTP,
  RequestOTPRequest,
  requestRegisterOTP,
  RequestRegisterOTPRequest,
  setAuthData,
  SetAuthDataRequest,
} from "./auth.api";
import {
  GetCreditErrorMessages,
  GetPanelsErrorMessages,
  GetPanelsErrors,
  GetTokenErrorMessages,
  RegisterPanelErrorMessages,
  RequestOTPErrorMessages,
  RequestOTPErrors,
  RequestRegisterOTPErrors,
  SetAuthDataErrorMessages,
} from "./auth.error";
import { RootState } from "../../utils/store";
import { AuthState } from "./auth.slice";
import { LocalStorageKeys } from "../../utils/utils";
import { validateStagingUser } from "./auth.utils";
import { sendYandexGoal, YandexGoal } from "../../yandex/yandex";

const saveToken = (token: string) => {
  localStorage.setItem(LocalStorageKeys.AccessToken, token);
};
const deleteToken = () => {
  localStorage.removeItem(LocalStorageKeys.AccessToken);
};

export const requestOtpThunk = createAsyncThunk(
  "Auth/RequestOtp",
  async (request: RequestOTPRequest, { rejectWithValue }) => {
    const { error, response } = await requestOTP(request);
    if (error) {
      if (error !== RequestOTPErrors.ErrNoBaleAccount) {
        toast.info(RequestOTPErrorMessages[error]);
      }
      return rejectWithValue(error);
    }
    localStorage.setItem(
      LocalStorageKeys.TransactionHash,
      response?.TransactionHash || ""
    );
    return { phone: request.phone, transactionHash: response?.TransactionHash };
  }
);

export const requestRegisterOtpThunk = createAsyncThunk(
  "Auth/RequestRegisterOtp",
  async (request: RequestRegisterOTPRequest, { rejectWithValue }) => {
    const { error, response } = await requestRegisterOTP(request);
    if (error) {
      if (error !== RequestRegisterOTPErrors.ErrNoBaleAccount) {
        toast.info(RequestRegisterOTPErrors[error]);
      }
      sendYandexGoal(YandexGoal.SmsRequest_error);
      return rejectWithValue(error);
    }
    sendYandexGoal(YandexGoal.SmsSent_success);
    return { phone: request.phone, transactionHash: response?.TransactionHash };
  }
);

export const getTokenThunk = createAsyncThunk(
  "Auth/GetToken",
  async (
    request: { otp: string; phoneFromParams?: string; noBaleAccount?: boolean },
    { rejectWithValue, getState }
  ) => {
    const phone =
      (getState() as RootState).auth.phone || request.phoneFromParams || "";
    const transactionHash =
      localStorage.getItem(LocalStorageKeys.TransactionHash) || "";
    const { response, error } = await getToken({
      phone,
      otpKey: request.otp,
      transactionHash,
    });
    if (error) {
      toast.info(GetTokenErrorMessages[error]);
      return rejectWithValue(error);
    }
    if (response) {
      saveToken(response.token);
      localStorage.removeItem(LocalStorageKeys.TransactionHash);
      if (response.has_panel) {
        return { state: AuthState.Success };
      } else {
        if (request.noBaleAccount) {
          sendYandexGoal(YandexGoal.SmsOtp_success);
          return { state: AuthState.BaleRegister };
        } else {
          return { state: AuthState.Register };
        }
      }
    }

    return response;
  }
);

export const registerPanelThunk = createAsyncThunk(
  "Auth/RegisterPanel",
  async (
    request: RegisterPanelRequest & { signUpBale: boolean },
    { rejectWithValue, getState }
  ) => {
    const { error } = await registerPanel(request);
    if (error) {
      toast.info(RegisterPanelErrorMessages[error]);
      sendYandexGoal(YandexGoal.BaleSignUp_error);
      return rejectWithValue(error);
    }
    if (request.signUpBale) {
      sendYandexGoal(YandexGoal.BaleSignUp_success);
    } else {
      sendYandexGoal(YandexGoal.Register_success);
    }
    return true;
  }
);

export const getPanelsThunk = createAsyncThunk(
  "Auth/GetPanels",
  async (request: GetPanelsRequest, { rejectWithValue, getState }) => {
    const { response, error } = await getPanels(request);
    if (error) {
      toast.info(GetPanelsErrorMessages[error]);
      return rejectWithValue(error);
    }
    // panel not registered
    if (!response) {
      deleteToken();
      rejectWithValue(GetPanelsErrors.NoRegisteredPanel);
    }
    validateStagingUser(response?.owner_id);
    return response;
  }
);

export const setAuthDataThunk = createAsyncThunk(
  "Auth/SetAuthData",
  async (request: SetAuthDataRequest, { rejectWithValue, getState }) => {
    const { response, error } = await setAuthData(request);
    if (error) {
      toast.info(SetAuthDataErrorMessages[error]);
      return rejectWithValue(error);
    }
    return response;
  }
);

export const getCreditThunk = createAsyncThunk(
  "Auth/GetCredit",
  async (request: GetCreditRequest, { rejectWithValue, getState }) => {
    const { response, error } = await getCredit(request);
    if (error) {
      toast.info(GetCreditErrorMessages[error]);
      return rejectWithValue(error);
    }
    return response;
  }
);
